import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import {
    SendOrderStatusAnalyticCustomDataEvent,
    SendOrderStatusAnalyticCustomPageEvent,
    SendOrderStatusAnalyticDataEvent,
    SendOrderStatusAnalyticPageEvent,
} from './order-status-analytics.actions';

import { CommonWindow } from '@core/domain/models/common-window.model';
import { getContext, getCurrencyInfo } from '@core/infrastructure/state/core.selectors';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { CustomData } from '@shared/analytics/models/analytics/analytics.models';
import { EventData } from '@shared/analytics/models/general/general.models';
import { PageViewDataObject } from '@shared/analytics/models/page-view/page-view.models';
import { imEventWrapperGetPageView } from '@shared/analytics/services/event/im-event-wrapper.helper';
import { CustomPageViewEvent, PageViewEvent, SendBasicAnalytics, SendCustomAnalytics } from '@shared/analytics/store/analytics.actions';
import { getCurrentOrder } from '@shared/order-payment/store/order-repayment.selectors';
import { getShopCart } from '@shared/shopcart/store/shopcart.selectors';
import { getUserSelector } from '@shared/user/store';

@Injectable()
export class OrderStatusAnalyticsEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        @Inject(WINDOW) private readonly window: CommonWindow,
    ) { }

    public sendOrderStatusAnalyticPageEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SendOrderStatusAnalyticPageEvent),
            map((action) => PageViewEvent({
                page: 'localizar',
                title: action.actionType,
                section: 'localizador_pedido',
                eventFrom: EventFrom.orderStatus,
            }))
        );
    });

    public sendOrderStatusAnalyticCustomPageEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SendOrderStatusAnalyticCustomPageEvent),
            concatLatestFrom(() => [
                this.store.select(getContext),
                this.store.select(getUserSelector),
                this.store.select(getShopCart),
                this.store.select(getCurrentOrder),
                this.store.select(routerPublicQueries.getUrlFromRouter),
                this.store.select(getCurrencyInfo),
            ]),
            map(([action, context, user, shopCart, order, urlFromRouter, currencyInfo]) => {
                const data = imEventWrapperGetPageView('localizador_pedido', 'localizar', action.actionType, context, user, shopCart, urlFromRouter, this.window, currencyInfo, order);
                const response: [PageViewDataObject, any] = [{
                    ...data,
                } as PageViewDataObject, action];
                return response;
            }),
            map(([pageViewEvent, action]) => {
                const data: PageViewDataObject = {
                    ...pageViewEvent,
                    order: { orderId: action.orderId, orderState: action.orderState },
                };
                return CustomPageViewEvent({
                    dataObject: data,
                    ecommerce: undefined,
                    eventFrom: EventFrom.orderStatus,
                });
            })
        );
    });

    public sendOrderStatusAnalyticCustomDataEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SendOrderStatusAnalyticCustomDataEvent),
            map((action) => {
                const eventData: EventData = {
                    cf: 'localizador_pedido',
                    category: 'localizador_pedido',
                    action: action.actionType,
                    label: action?.orderId,
                    eventFrom: EventFrom.orderStatus,
                };
                const customData: CustomData = {
                    order: {
                        orderId: action?.orderId,
                        orderState: action?.orderState,
                    },
                };
                return SendCustomAnalytics({
                    eventData,
                    customData,
                });
            })
        );
    });

    public sendOrderStatusAnalyticDataEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SendOrderStatusAnalyticDataEvent),
            map((action) => {
                return SendBasicAnalytics({
                    cf: 'localizador_pedido',
                    category: 'localizador_pedido',
                    action: action.actionType,
                    eventFrom: EventFrom.orderStatus,
                });
            })
        );
    });
}
